import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import LogoRow from '@/assets/store/icon/logo-row.svg';
import BannerInfo from '@/domains/mobile-web/components/BannerInfo/BannerInfo';
import CardProduct from '@/domains/mobile-web/components/CardProduct/CardProduct';
import CardProductBundling from '@/domains/mobile-web/components/CardProductBundling/CardProductBundling';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import { useCartCount } from '@/domains/mobile-web/hooks/useCart';
import { useProduct, useProductPages } from '@/domains/mobile-web/hooks/useProducts';
import type { ProductSummary } from '@/domains/mobile-web/types/product.types';
import { availabilityStatus } from '@/domains/mobile-web/utils/price';
import { cn } from '@/lib/utils';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';
import Seo from '@/shared/components/ui/Seo/Seo';
import { SkeletonCardProduct, SkeletonCardProductBundling } from '@/shared/components/ui/Skeleton';

// The list only carries the thumbnail; the big Home card shows the full-size cover from the detail
// (the thumbnail stays until it loads, or if it fails). Lihat Paket then opens from the same cache.
const HomeBundle = ({ product }: { product: ProductSummary }) => {
  const navigate = useNavigate();
  const { data: detail } = useProduct(product.slug);

  return (
    <CardProductBundling
      type="vertical"
      packet={availabilityStatus(product.availability)}
      product={product}
      imageUrl={detail?.images[0]}
      onCollect={() => navigate('/store/book')}
      onPacket={() => navigate(`/store/product-detail/${product.slug}`)}
    />
  );
};

const Home = () => {
  // Bundles always come first in the catalogue, so the first page holds them
  const { data, isLoading } = useProductPages();
  const bundles = (data?.pages[0]?.rows ?? []).filter((row) => row.product_type === 'bundle');
  const navigate = useNavigate();
  const cartCount = useCartCount();

  return (
    <>
      <Seo
        title="Toko Buku Online, Donasi Buku & Afiliasi"
        description="Rumah Kebijaksanaan, toko buku online: cari dan beli buku pilihan, dukung program donasi buku, dan dapatkan komisi lewat program afiliasi."
        path="/store/home"
      />
      <HeaderMobile>
        <TopBarHeader
          type="textBucket"
          totalProduct={cartCount}
          onAction={() => navigate('/store/cart')}
          showLogo={true}
        />
      </HeaderMobile>

      <WrapperMobile>
        <div className="overflow-hidden rounded-[16px] p-6   mt-4 w-full bg-[url('@/assets/store/images/hero-img.webp')]  bg-center bg-no-repeat bg-cover">
          <article className="text-white reg12 lg:text-[16px]  gap-3 flex flex-col items-start">
            {/* The page heading is the logo; search engines read its alt text as the h1 */}
            <h1 className="m-0 leading-none">
              <img
                src={LogoRow}
                alt="Rumah Kebijaksanaan, toko buku online"
                className="w-auto h-auto"
                loading="eager"
                width="160"
                height="40"
              />
            </h1>
            <p>
              Baitul Hikmah (Bayt al-Hikma) adalah sebutan untuk ‘Perpustakaan Baghdad’ yang
              didirikan 1.196 tahun silam. Perpustakaan ini menjadi gerbang pembuka bagi pesatnya
              pertumbuhan ilmu pengetahuan, asimilasi budaya, serta renaisans sains dan seni. Lebih
              dari itu, Baitul Hikmah telah menjadi landasan bagi metodologi eksperimental sains
              modern.
            </p>
            <p>
              Kini, kami mulai membangun <strong>'Rumah Kebijaksanaan Indonesia'</strong>
            </p>
            <p>
              Proyek pertama dari Rumah Kebijaksanaan akan diterbitkan pada bulan Desember
              mendatang. Kami membuka pendaftaran Pre-Order dengan penawaran diskon spesial 50%
            </p>
          </article>
        </div>

        <BannerInfo desc="Masa Pre-Order ini bersifat terbatas dan akan otomatis ditutup apabila kuota yang dialokasikan telah habis." />

        {isLoading ? (
          <>
            <SkeletonCardProductBundling />
          </>
        ) : (
          <>
            {bundles.map((item) => (
              <HomeBundle key={item.id} product={item} />
            ))}
          </>
        )}

        <p className="title3 text-start text-text-grey  lg:text-center">
          Dengan melakukan pemesanan, Anda menyetujui transaksi pembelian atau pembagian informasi
          melalui PT. Rumah Kebijaksanaan Indonesia sebagai merchant resmi, serta menyetujui Syarat
          dan Ketentuan dan Kebijakan Privasi yang berlaku.
        </p>
      </WrapperMobile>
    </>
  );
};

export default Home;
